<script setup>
import { Card, Label, Input, Button, Icon, Modal, MediaUploaderButton, Spin } from '@components'
import { ref, reactive, computed, onMounted, watch } from 'vue'

// Route.
import { useRoute, useRouter } from 'vue-router'
const route = useRoute()
const router = useRouter()

// Customer Store.
import { useCustomer } from '@store/customer.js'
const $customer = useCustomer()

import { useSettings } from '@store/settings'
const $settings = useSettings()

// State.
const state = reactive({})

const isCustomerChanged = ref(false)

const customer = ref({
    display_name: '',
    meta: {
        status: 'publish'
    },
})



const isNew = computed(() => {
    return route.params.customerId ? false : true
})

import { validationCheck, regex } from '@utils'
const save = async () => {
    if (!customer.value.customer_id && !isNew.value) return Toast.error(`No ${String($settings.labels.customer?.singular).toLowerCase()} id found`)

    // validation
    let { errors } = validationCheck([
        {
            condition: !customer.value.meta.first_name,
            message: `${$settings.labels.customer?.singular} first name is required`
        },
        {
            condition: !customer.value.meta.last_name,
            message: `${$settings.labels.customer?.singular} last name is required`
        },
        {
            condition: !customer.value.email,
            message: `${$settings.labels.customer?.singular} email address is required`
        },
        {
            condition: !regex.email.test(customer.value.email),
            message: `Invalid ${String($settings.labels.customer?.singular).toLowerCase()} email format`,
        },
        {
            condition: !customer.value.meta.phone,
            message: `${$settings.labels.customer?.singular} phone number is required`
        },
        {
            condition: !regex.phone.test(customer.value.meta.phone),
            message: `Invalid ${String($settings.labels.customer?.singular).toLowerCase()} phone number format`,
        },
    ])
    if (errors) return state.saving = false

    customer.value.display_name = `${customer.value.meta.first_name} ${customer.value.meta.last_name}`

    // after validation submit form
    const response = isNew.value
        ? await $customer.model().create(customer.value)
        : await customer.value.update(customer.value, customer.value.customer_id)

    state.saving = false
    Log('RESPONSE', response)

    if (response.success) {
        Toast.success(isNew.value
            ? `${$settings.labels.customer?.singular} added successfully`
            : `${$settings.labels.customer?.singular} details updated successfully`
        )

        // if (isNew.value) {
        //     await router.push({ name: 'customer.edit', params: { customerId: response.data.customer_id } })
        //     customer.value = await $customer.fetchCustomer(response.data.customer_id)
        // }

        // back to list page [issue #82]
        router.push({ name: 'customer.list' })
    } else {
        Toast.error(response.message)
    }
}

const fetchCustomer = async () => {
    customer.value = await $customer.fetchCustomer(route.params.customerId)
}

onMounted(async () => {
    if (isNew.value) return

    state.loading = true
    await fetchCustomer()
    Log('CUSTOMER', customer.value)
    state.loading = false
    isCustomerChanged.value = false
})

watch(customer, (newVal, oldVal) => {
    isCustomerChanged.value = true
    console.log('FORM CHANGED')
}, { deep: true })

const backButton = async () => {
    if (isCustomerChanged.value) {
        const choice = await Prompt.fire({
            title: 'Unsaved Changes Detected',
            message: `You have unsaved changes. Any unsaved changes will be lost. <br> Are you sure you want to continue?`,
            ok: 'Yes, proceed',
            cancel: 'No, cancel',
        });
        if (!choice.ok) return;
    }
    
    const backToRoute = route.query.backTo ? route.query.backTo : { name: 'customer.list' };
    router.push(backToRoute);
}

const publish = async () => {
    customer.value.meta.status = 'publish'
    state.saving = 'publish'
    await save()
    isCustomerChanged.value = false
}

const publishButton = computed(() => {
    if (customer.value.meta.status !== 'publish' || isNew.value) {
        return state.saving == 'publish' ? 'Publishing' : 'Publish'
    }

    return state.saving == 'publish' ? 'Updating' : 'Update'
})
</script>

<template>
    <Modal :loading="state.loading">

        <!-- header  -->
        <!-- <template #header>
           <div class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between shadow-[0px_3px_0px_0px_#eaecf0]">
                <div @click="backButton" class="cursor-pointer flex items-center gap-5 text-[#101828] text-lg font-semibold">
                    <Icon name="arrowNarrowLeft" size="24px"></Icon> Back
                </div>
            </div>
        </template> -->
        <template #header>
            <div class="px-8 py-6 border-b-[#EAECF0] border-b flex items-center justify-between shadow-[0px_3px_0px_0px_#eaecf0]">
                <div @click="backButton" class="cursor-pointer flex items-center gap-5 text-[#101828] text-lg font-semibold">
                    <Icon name="arrowNarrowLeft" size="24px"></Icon> Back
                </div>
                <div v-if="!state.loading" class="flex items-center h-10 gap-4">
                    <Button size="lg" type="primary" radius="square" @click.prevent="publish">{{ publishButton }}</Button>
                    <div @click="backButton">
                        <Icon name="xClose" size="20px"></Icon>
                    </div>
                </div>
                <div v-else class="flex items-center h-10">
                    <Spin size="25px" />
                </div>
            </div>
        </template>

        <template #default>
            <div class="my-[-28px] md:w-[736px] m-auto" :class="{ 'cursor-wait': state.loading }">
                <div class="mb-7 flex items-center justify-between gap-3 text-[#344054] text-2xl font-semibold leading-8">
                    <div class="flex items-center gap-3">
                        {{ isNew ? 'Add' : 'Update' }} {{ String($settings.labels.customer?.singular).toLowerCase() }}
                    </div>
                </div>

                <form @submit.prevent="save">
                    <Card>
                        <!-- {{ customer }} -->
                        <div class="relative w-[128px] h-[128px] mb-10" v-if="customer.meta">
                            <img v-if="customer.meta.profile_image_url" :src="customer.meta.profile_image_url" class=" rounded-full border-8 border-[#fff] w-[128px] h-[128px]">
                            <div v-else-if="customer.display_name" class="bg-[#9E77ED] text-white rounded-full border-8 border-[#fff] w-[128px] h-[128px] flex items-center justify-center uppercase font-sans text-5xl font-bold">
                                {{ customer.display_name?.split('')[0] }}
                            </div>
                            <img v-else :src="Image('dummy-profile.png')" class="bg-[#9E77ED] rounded-full border-8 border-[#fff] w-[128px] h-[128px]">

                            <div class="bg-[#9E77ED] cursor-pointer absolute bottom-0 right-0 border-[3px] border-[#fff] text-[#fff] w-10 h-10 rounded-full flex items-center justify-center">
                                <MediaUploaderButton :crop="true" :ratio="1 / 1" name="customer_picture" v-model="customer.meta.profile_image_url">
                                    <Icon name="edit03" size="20px"></Icon>
                                </MediaUploaderButton>
                            </div>
                        </div>

                        <div class="flex flex-col gap-5 mb-5 md:grid md:grid-cols-2" v-if="customer.meta">
                            <div class="w-full">
                                <Label>First Name <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input v-model="customer.meta.first_name" placeholder="ex. Jhon" type="text" size="sm" class="mt-1.5"></Input>
                            </div>
                            <div class="w-full">
                                <Label>Last Name <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input v-model="customer.meta.last_name" placeholder="ex. Doe" type="text" size="sm" class="mt-1.5"></Input>
                            </div>
                            <div class="w-full">
                                <Label>Email Address <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input v-model="customer.email" placeholder="Enter email" type="email" size="sm" class="mt-1.5" iconLeft="mail01"></Input>
                            </div>
                            <div class="w-full">
                                <Label>Phone Number <div class="text-[#D92D20] inline-block">*</div></Label>
                                <Input v-model="customer.meta.phone" placeholder="Enter phone number" type="text" size="sm" class="mt-1.5" iconLeft="phoneCall01"></Input>
                            </div>
                            <div class="w-full col-span-2">
                                <Label>Address</Label>
                                <Input v-model="customer.meta.address" placeholder="Enter customer address" type="text" size="sm" class="mt-1.5" iconLeft="markerPin01"></Input>
                            </div>
                        </div>
                    </Card>

                    <div class="flex items-center gap-7">
                        <Button size="xl" type="secondary_gray" radius="square" classButton="!w-[180px] mt-10 justify-between" @click.prevent="backButton">
                            <div class="w-[70%]">Cancel</div>
                            <template #before>
                                <Icon name="arrowNarrowLeft" size="20px"></Icon>
                            </template>
                        </Button>
                        <Button submit size="xl" type="primary" radius="square" classButton="w-full mt-10 justify-between">
                            <div class="w-[95%]">{{ state.loading ? 'Loading...' : isNew ? 'Publish' : 'Update' }}</div>
                        </Button>
                    </div>
                </form>

            </div>
        </template>
    </Modal>
</template>
